import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { DEAL_TYPE_LABELS, DEAL_TYPE_OPTIONS } from '@/lib/dealTypes'
import { GiftingBonusEditor } from '@/components/gifting/GiftingBonusEditor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import api from '@/lib/api'
import { useToast } from '@/components/ui/use-toast'
import type { CampaignBriefData } from '@/hooks/useCampaign'

const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Facebook']

const OBJECTIVE_OPTIONS = [
  'Brand Awareness',
  'Product Launch',
  'Sales / Conversions',
  'App Downloads',
  'Event Promotion',
  'NIL Brand Partnership',
  'Long-Term Ambassador Consideration',
]

const TONE_OPTIONS = [
  'Authentic / Personal',
  'High-energy',
  'Educational',
  'Inspirational',
  'Humorous',
  'Competitive',
  'Lifestyle-focused',
  'Community-driven',
]

const GENDER_OPTIONS = ['All genders', 'Primarily male', 'Primarily female', 'Non-binary focused']

const LOCATION_OPTIONS = ['National', 'Regional', 'Campus-specific']

const DEFAULT_BRIEF: CampaignBriefData = {
  description: '',
  objectives: [],
  primaryKpi: '',
  targetAgeRange: '',
  targetGender: 'All genders',
  targetLocation: 'National',
  targetAudienceDescription: '',
  primaryMessage: '',
  supportingPoints: ['', '', ''],
  cta: '',
  tones: [],
  creativeDirection: '',
  additionalNotes: '',
}

const formatDateForInput = (dateStr: string) => {
  if (!dateStr) return ''
  const [datePart] = dateStr.split('T')
  const [year, month, day] = datePart.split('-')

  if (!year || !month || !day) return ''

  return `${year}-${month}-${day}`
}

interface EditCampaignModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaign: any
  onUpdate: () => void
}

export const EditCampaignModal: React.FC<EditCampaignModalProps> = ({
  open,
  onOpenChange,
  campaign,
  onUpdate,
}) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)

  const [basics, setBasics] = useState({
    name: campaign.name || '',
    startDate: formatDateForInput(campaign.startDate || ''),
    endDate: formatDateForInput(campaign.endDate || ''),
    budget: campaign.budget?.toString() || '',
    socialPlatform: Array.isArray(campaign.socialPlatform)
      ? campaign.socialPlatform
      : campaign.socialPlatform
        ? [campaign.socialPlatform]
        : [],
    dealType: (campaign.dealType || 'gifting') as string,
    openToApplications: !!campaign.openToApplications,
    applicationDeadline: formatDateForInput(campaign.applicationDeadline || ''),
    payout: campaign.payout != null ? String(campaign.payout) : '',
  })
  const [giftingBonuses, setGiftingBonuses] = useState<{ views: number; amount: number }[] | null>(
    campaign.giftingTerms?.bonuses ?? null,
  )

  const existingBrief = campaign.campaignBrief
  const [brief, setBrief] = useState<CampaignBriefData>({
    ...DEFAULT_BRIEF,
    ...(existingBrief ?? {}),
    supportingPoints: existingBrief?.supportingPoints?.length
      ? [...existingBrief.supportingPoints, '', '', ''].slice(0, 3)
      : ['', '', ''],
  })

  const updateBrief = (patch: Partial<CampaignBriefData>) =>
    setBrief((prev) => ({ ...prev, ...patch }))

  const toggleObjective = (obj: string) => {
    updateBrief({
      objectives: brief.objectives.includes(obj)
        ? brief.objectives.filter((o) => o !== obj)
        : [...brief.objectives, obj],
    })
  }

  const toggleTone = (tone: string) => {
    if (brief.tones.includes(tone)) {
      updateBrief({ tones: brief.tones.filter((t) => t !== tone) })
    } else if (brief.tones.length < 3) {
      updateBrief({ tones: [...brief.tones, tone] })
    }
  }

  const updateSupportingPoint = (index: number, value: string) => {
    const pts = [...brief.supportingPoints]
    pts[index] = value
    updateBrief({ supportingPoints: pts })
  }

  const inputClassName =
    'border-foreground/15 bg-card text-foreground placeholder:text-gray-500 focus-visible:ring-[#C0FD72]/40 focus-visible:ring-offset-0'

  const chipBaseClassName =
    'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors bg-card border-foreground/15 text-gray-700 hover:border-[#C0FD72]/50 hover:text-[#3F7A0A]'

  const handleSave = async () => {
    setLoading(true)
    try {
      await api.patch(`/marketer/campaigns/${campaign.id}`, {
        name: basics.name,
        startDate: basics.startDate || null,
        endDate: basics.endDate || null,
        budget: basics.budget ? Number(basics.budget) : null,
        socialPlatform: basics.socialPlatform,
        campaignBrief: brief,
        dealType: basics.dealType,
        openToApplications: basics.openToApplications,
        applicationDeadline: basics.applicationDeadline || null,
        payout: basics.dealType === 'gifting' ? 0 : basics.payout ? Number(basics.payout) : null,
        ...(basics.dealType === 'gifting' && giftingBonuses ? { giftingBonuses } : {}),
      })
      toast({ title: 'Campaign updated' })
      onUpdate()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating campaign:', error)
      toast({ title: 'Error', description: 'Failed to update campaign', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-foreground/15 bg-card text-foreground">
        <DialogHeader className="border-b border-foreground/10 pb-4">
          <DialogTitle className="text-xl text-foreground">Edit Campaign</DialogTitle>
        </DialogHeader>

        <div className="space-y-8 py-2">
          {/* Campaign Basics */}
          <div className="space-y-4">
            <h3 className="font-semibold uppercase text-xs text-gray-600 tracking-wide">
              Campaign Basics
            </h3>

            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-gray-700">
                Campaign Name
              </Label>
              <Input
                id="edit-name"
                value={basics.name}
                onChange={(e) => setBasics((p) => ({ ...p, name: e.target.value }))}
                className={inputClassName}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-start" className="text-gray-700">
                  Start Date
                </Label>
                <Input
                  id="edit-start"
                  type="date"
                  value={basics.startDate}
                  onChange={(e) => setBasics((p) => ({ ...p, startDate: e.target.value }))}
                  className={inputClassName}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end" className="text-gray-700">
                  End Date
                </Label>
                <Input
                  id="edit-end"
                  type="date"
                  value={basics.endDate}
                  onChange={(e) => setBasics((p) => ({ ...p, endDate: e.target.value }))}
                  className={inputClassName}
                />
              </div>
            </div>

            {/* Deal type and applications */}
            <div className="space-y-4 rounded-lg border border-foreground/10 p-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Deal type</Label>
                <div className="flex flex-wrap gap-2">
                  {DEAL_TYPE_OPTIONS.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setBasics((p) => ({ ...p, dealType: d.id }))}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                        basics.dealType === d.id
                          ? 'border-[#C0FD72] bg-[#C0FD72] text-black'
                          : 'border-foreground/15 bg-card text-gray-700 hover:border-[#C0FD72]/50',
                      )}
                    >
                      {DEAL_TYPE_LABELS[d.id]}
                    </button>
                  ))}
                </div>
              </div>
              {basics.dealType === 'gifting' ? (
                <GiftingBonusEditor
                  compact
                  value={giftingBonuses}
                  onChange={(t) => setGiftingBonuses(t)}
                />
              ) : null}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="edit-open" className="text-gray-700">
                    Open to athlete applications
                  </Label>
                  <p className="text-xs text-gray-500">
                    Athletes who fit see this in their Marketplace and apply.
                  </p>
                </div>
                <Switch
                  id="edit-open"
                  checked={basics.openToApplications}
                  onCheckedChange={(checked) =>
                    setBasics((p) => ({ ...p, openToApplications: checked }))
                  }
                />
              </div>
              {basics.openToApplications && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-payout" className="text-gray-700">
                      {basics.dealType === 'licensing'
                        ? 'Pay per athlete, per month'
                        : 'Pay per athlete'}
                    </Label>
                    {basics.dealType === 'gifting' ? (
                      <p className="rounded-md border border-foreground/10 px-3 py-2 text-sm text-gray-500">
                        Product, plus bonuses
                      </p>
                    ) : (
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500 text-sm">$</span>
                        <Input
                          id="edit-payout"
                          type="number"
                          className={cn('pl-7', inputClassName)}
                          value={basics.payout}
                          onChange={(e) => setBasics((p) => ({ ...p, payout: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-deadline" className="text-gray-700">
                      Applications close
                    </Label>
                    <Input
                      id="edit-deadline"
                      type="date"
                      className={inputClassName}
                      value={basics.applicationDeadline}
                      onChange={(e) =>
                        setBasics((p) => ({ ...p, applicationDeadline: e.target.value }))
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-budget" className="text-gray-700">
                  Budget
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500 text-sm">$</span>
                  <Input
                    id="edit-budget"
                    type="number"
                    className={cn('pl-7', inputClassName)}
                    value={basics.budget}
                    onChange={(e) => setBasics((p) => ({ ...p, budget: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Platform</Label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const current = basics.socialPlatform
                        setBasics((prev) => ({
                          ...prev,
                          socialPlatform: current.includes(p)
                            ? current.filter((x: string) => x !== p)
                            : [...current, p],
                        }))
                      }}
                      className={cn(
                        chipBaseClassName,
                        basics.socialPlatform.includes(p)
                          ? 'border-[#C0FD72] bg-[#C0FD72] text-black hover:text-black'
                          : '',
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-muted" />

          {/* Campaign Brief */}
          <div className="space-y-6">
            <h3 className="font-semibold uppercase text-xs text-gray-600 tracking-wide">
              Campaign Brief
            </h3>

            {/* Overview */}
            <div className="space-y-2">
              <Label htmlFor="edit-description" className="text-gray-700">
                Overview
              </Label>
              <Textarea
                id="edit-description"
                placeholder="Briefly describe the campaign — what it's about, what you're launching or promoting, and the overall vibe. 2–3 sentences."
                value={brief.description}
                onChange={(e) => updateBrief({ description: e.target.value })}
                className={cn('min-h-[80px] resize-none', inputClassName)}
              />
            </div>

            {/* Objectives */}
            <div className="space-y-2">
              <Label className="text-gray-700">Objectives</Label>
              <div className="flex flex-wrap gap-2">
                {OBJECTIVE_OPTIONS.map((obj) => {
                  const active = brief.objectives.includes(obj)
                  return (
                    <button
                      key={obj}
                      type="button"
                      onClick={() => toggleObjective(obj)}
                      className={cn(
                        chipBaseClassName,
                        active ? 'border-[#C0FD72] bg-[#C0FD72] text-black hover:text-black' : '',
                      )}
                    >
                      {obj}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-kpi" className="text-gray-700">
                Primary KPI (optional)
              </Label>
              <Input
                id="edit-kpi"
                placeholder="e.g. awareness, traffic, app installs, conversions..."
                value={brief.primaryKpi}
                onChange={(e) => updateBrief({ primaryKpi: e.target.value })}
                className={inputClassName}
              />
            </div>

            <Separator className="bg-muted" />

            {/* Target Audience */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-gray-800">Target Audience</Label>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-age" className="text-sm font-normal text-gray-600">
                    Age Range
                  </Label>
                  <Input
                    id="edit-age"
                    placeholder="e.g. 18–24, College-aged"
                    value={brief.targetAgeRange}
                    onChange={(e) => updateBrief({ targetAgeRange: e.target.value })}
                    className={inputClassName}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-normal text-gray-600">Gender</Label>
                  <Select
                    value={brief.targetGender}
                    onValueChange={(v) => updateBrief({ targetGender: v })}
                  >
                    <SelectTrigger className={inputClassName}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-foreground/15 bg-card text-foreground">
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-normal text-gray-600">Location</Label>
                <div className="flex gap-2">
                  {LOCATION_OPTIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => updateBrief({ targetLocation: loc })}
                      className={cn(
                        chipBaseClassName,
                        brief.targetLocation === loc
                          ? 'border-[#C0FD72] bg-[#C0FD72] text-black hover:text-black'
                          : '',
                      )}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-audience-desc" className="text-sm font-normal text-gray-600">
                  Audience Description
                </Label>
                <Textarea
                  id="edit-audience-desc"
                  placeholder="e.g. Active college students who follow fitness content..."
                  value={brief.targetAudienceDescription}
                  onChange={(e) => updateBrief({ targetAudienceDescription: e.target.value })}
                  className={cn('min-h-[70px] resize-none', inputClassName)}
                />
              </div>
            </div>

            <Separator className="bg-muted" />

            {/* Key Message */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-gray-800">Key Message</Label>

              <div className="space-y-2">
                <Label
                  htmlFor="edit-primary-message"
                  className="text-sm font-normal text-gray-600"
                >
                  Primary Message
                </Label>
                <Input
                  id="edit-primary-message"
                  placeholder="e.g. Our product helps athletes recover faster after intense training."
                  value={brief.primaryMessage}
                  onChange={(e) => updateBrief({ primaryMessage: e.target.value })}
                  className={inputClassName}
                />
                <p className="text-xs text-gray-500">
                  1 sentence — the core thing athletes should communicate
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-normal text-gray-600">
                  Supporting Points (optional)
                </Label>
                <div className="space-y-2">
                  {brief.supportingPoints.map((pt, i) => (
                    <Input
                      key={i}
                      placeholder={`Supporting point ${i + 1}`}
                      value={pt}
                      onChange={(e) => updateSupportingPoint(i, e.target.value)}
                      className={inputClassName}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-cta" className="text-sm font-normal text-gray-600">
                  Call-to-Action (optional)
                </Label>
                <Input
                  id="edit-cta"
                  placeholder='e.g. "Shop now", "Use code ATHLETE15"'
                  value={brief.cta}
                  onChange={(e) => updateBrief({ cta: e.target.value })}
                  className={inputClassName}
                />
              </div>
            </div>

            <Separator className="bg-muted" />

            {/* Tone */}
            <div className="space-y-2">
              <Label>
                Tone <span className="text-gray-500 font-normal">(up to 3)</span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {TONE_OPTIONS.map((tone) => {
                  const active = brief.tones.includes(tone)
                  const maxed = !active && brief.tones.length >= 3
                  return (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => toggleTone(tone)}
                      disabled={maxed}
                      className={cn(
                        chipBaseClassName,
                        active
                          ? 'border-[#C0FD72] bg-[#C0FD72] text-black hover:text-black'
                          : maxed
                            ? 'opacity-40 cursor-not-allowed border-foreground/15 text-gray-500 hover:border-foreground/15 hover:text-gray-500'
                            : '',
                      )}
                    >
                      {tone}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Creative Direction */}
            <div className="space-y-2">
              <Label htmlFor="edit-creative-direction" className="text-gray-700">
                Creative Direction
              </Label>
              <Textarea
                id="edit-creative-direction"
                placeholder="e.g. Show how you incorporate the product into your training routine. We want to see it in action — before, during, or after a workout."
                value={brief.creativeDirection}
                onChange={(e) => updateBrief({ creativeDirection: e.target.value })}
                className={cn('min-h-[90px] resize-none', inputClassName)}
              />
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="edit-notes" className="text-gray-700">
                Additional Notes (optional)
              </Label>
              <Textarea
                id="edit-notes"
                placeholder="Anything else athletes should know — dos and don'ts, required disclosures, competitor exclusions, etc."
                value={brief.additionalNotes}
                onChange={(e) => updateBrief({ additionalNotes: e.target.value })}
                className={cn('min-h-[70px] resize-none', inputClassName)}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-foreground/10 pt-4">
          <Button variant="dark-outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="lime" onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
